import React, { useRef } from "react"

import { useCreatorResolver } from "@/organization"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  toast,
} from "@coval/ui"
import { AlertTriangle, Download, Upload } from "lucide-react"

import { logger } from "@/lib/logger"
import { ResourceHeaderActions } from "@/components/resource-header/ResourceHeaderActions"
import type { ResourceAttribution } from "@/types_api/backend/transport/get_metric_response"
import { resourceHeaderAttribution } from "@/utils/resourceAttribution"

const log = logger("DashboardActions")

interface DashboardActionsProps {
  onClearDashboard: () => void
  onDeleteDashboard: () => void | Promise<void>
  onExportDashboard: () => void
  onImportDashboard: (file: File) => Promise<void>
  widgetCount: number
  isDefaultDashboard: boolean
  dashboardName: string
  dashboardId: string
  dashboardUrl: string
  attribution?: ResourceAttribution | null
}

export const DashboardActions: React.FC<DashboardActionsProps> = ({
  onDeleteDashboard,
  onExportDashboard,
  onImportDashboard,
  widgetCount,
  isDefaultDashboard,
  dashboardName,
  dashboardId,
  dashboardUrl,
  attribution,
}) => {
  const creatorResolver = useCreatorResolver({ canonical: true })
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      try {
        await onImportDashboard(file)
      } catch (error) {
        log.error("Import failed", error)
      }
    }
    event.target.value = ""
  }

  const handleDeleteConfirm = async () => {
    setIsDeleting(true)
    try {
      await onDeleteDashboard()
      setIsDeleteOpen(false)
    } catch (error) {
      toast.error({
        title: "Could not delete dashboard",
        error,
        fallback: "Could not delete the dashboard.",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <ResourceHeaderActions
        additionalActions={[
          {
            label: "Export dashboard",
            onSelect: onExportDashboard,
            disabled: widgetCount === 0,
            icon: <Download />,
          },
          {
            label: "Import dashboard",
            onSelect: handleImportClick,
            icon: <Upload />,
          },
        ]}
        resourceId={dashboardId}
        resourceUrl={dashboardUrl}
        attribution={resourceHeaderAttribution(attribution, creatorResolver)}
        delete={{
          label: "Delete dashboard",
          onDelete: () => setIsDeleteOpen(true),
          pending: isDeleting,
          disabled: isDefaultDashboard,
          "data-testid": "dashboard-delete-button",
        }}
        menuLabel="Dashboard actions"
        menuTestId="dashboard-actions-menu"
      />

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={(event) => {
          void handleFileChange(event)
        }}
        style={{ display: "none" }}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={isDeleteOpen}
        onOpenChange={(open) => {
          if (!open && isDeleting) {
            return
          }
          setIsDeleteOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-coval-danger-text" />
              Delete Dashboard
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete the{" "}
              <strong>"{dashboardName}"</strong> dashboard? This action cannot
              be undone. All widgets and configurations will be permanently
              removed.
              {widgetCount > 0 && (
                <>
                  {" "}
                  This dashboard contains {widgetCount} widget
                  {widgetCount !== 1 ? "s" : ""}.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="ghost" disabled={isDeleting}>
                Cancel
              </Button>
            </AlertDialogCancel>
            <Button
              variant="danger"
              onClick={() => void handleDeleteConfirm()}
              disabled={isDeleting}
              data-testid="dashboard-delete-confirm-button"
            >
              Delete Dashboard
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
